import {WriteButton, WriteIconButton} from '../../common/buttons/WriteActions';
import {getCurrencySymbol} from '../../../common/currency';
import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Autocomplete, Box, Card, CardContent, InputAdornment, Snackbar, Stack, TextField, Typography} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import {DecorationGuidDto, SaveDecorationGuidRequest} from '../../../models/Decorations/IDecorationGuid';
import {ItemLightDto, WeightUnit} from '../../../models/Items/IItem';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useCurrency} from '../../../hooks/useCurrency';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {LocalizedDecimalField} from '../../common/inputs/LocalizedDecimalField';
import {toSaveDecorationGuidRequest} from '../../../mappers/decorationGuid.mapper';
import {selectedIngredientUnit} from '../shared/ingredientSelection';

type Props = {
    availableItems?: ItemLightDto[];
    changeMode: () => void;
    initialData?: DecorationGuidDto | null;
    onSubmit: (data: SaveDecorationGuidRequest) => Promise<void>;
};
type DraftItem = {clientId: string; id?: string; itemId: string; name?: string; quantity: number; unit: WeightUnit | ''; deleted?: boolean};
type DraftPriceOption = {clientId: string; id?: string; quantity: number; price: number; deleted?: boolean};
type DecorationDraft = {id?: string; name: string; description: string; items: DraftItem[]; priceOptions: DraftPriceOption[]};
type ErrorMap = Record<string, string>;
type TouchedMap = Record<string, boolean>;
const ingredientRowSx = {
    display: 'grid',
    gridTemplateColumns: {xs: 'minmax(0, 1fr) 110px 44px', sm: 'minmax(0, 1fr) 140px 44px'},
    gridTemplateAreas: '"name quantity action"',
    gap: 1,
    alignItems: 'start',
    width: '100%',
    minWidth: 0,
};
const ingredientActionSx = {gridArea: 'action', width: 44, height: 44, minWidth: 44, minHeight: 44, mt: 0.75};
const animatedHelperSx = (visible: boolean) => ({
    maxHeight: visible ? 64 : 0,
    opacity: visible ? 1 : 0,
    mt: visible ? 0.5 : 0,
    overflow: 'hidden',
    transition: 'max-height 180ms ease, opacity 180ms ease, margin-top 180ms ease',
});

const newItem = (): DraftItem => ({
    clientId: crypto.randomUUID(),
    itemId: '',
    name: '',
    quantity: undefined as unknown as number,
    unit: '',
});
const defaultDecoration = (): DecorationDraft => ({
    name: '',
    description: '',
    items: [newItem()],
    priceOptions: [],
});
const decorationForForm = (source?: DecorationGuidDto | null): DecorationDraft => source
    ? {...source, items: (source.items ?? []).map(item => ({...item, clientId: crypto.randomUUID()})), priceOptions: (source.priceOptions ?? []).map(option => ({...option, clientId: crypto.randomUUID()}))}
    : defaultDecoration();
const decorationSnapshot = (decoration?: DecorationDraft | DecorationGuidDto | null) => ({
    name: decoration?.name.trim() ?? '',
    description: decoration?.description.trim() ?? '',
    items: (decoration?.items ?? [])
        .map(item => ({id: item.id, itemId: item.itemId, quantity: item.quantity ?? null, deleted: 'deleted' in item ? item.deleted : false})),
    priceOptions: (decoration?.priceOptions ?? []).map(option => ({id: option.id, quantity: option.quantity, price: option.price, deleted: 'deleted' in option ? option.deleted : false})),
});

export default function DecorationForm({availableItems = [], initialData, onSubmit}: Props) {
    const {currency} = useCurrency();
    const {t} = useAppTranslation();
    const [form, setForm] = useState<DecorationDraft>(() => decorationForForm(initialData));
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState<TouchedMap>({});
    const [serverErrors, setServerErrors] = useState<ErrorMap>({});
    const [submitError, setSubmitError] = useState('');
    const [traceId, setTraceId] = useState('');
    const [serverToast, setServerToast] = useState('');

    useEffect(() => {
        setForm(decorationForForm(initialData));
        setSubmitted(false);
        setTouched({});
        setServerErrors({});
        setSubmitError('');
        setTraceId('');
    }, [initialData]);

    const isDirty = JSON.stringify(decorationSnapshot(form)) !== JSON.stringify(decorationSnapshot(initialData));
    const errors = useMemo<ErrorMap>(() => {
        const next: ErrorMap = {};
        if (!form.name.trim()) next.name = t('recipes:validation_required');
        else if (form.name.trim().length > 200) next.name = t('recipes:validation_max_length', {max: 200});
        const seen = new Set<string>();
        form.items.forEach((item, index) => {
            if (item.deleted) return;
            const nameField = `item.${index}.name`;
            const quantityField = `item.${index}.quantity`;
            if (!item.itemId || !item.name?.trim()) next[nameField] = t('recipes:validation_select_ingredient');
            if (item.itemId && seen.has(item.itemId)) next[nameField] = t('recipes:validation_duplicate_ingredient');
            if (item.itemId) seen.add(item.itemId);
            if (item.quantity === undefined || item.quantity === null || String(item.quantity).trim() === '' || !Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) {
                next[quantityField] = t('recipes:validation_positive_number');
            }
        });
        const activeOptions = form.priceOptions.filter(option => !option.deleted);
        activeOptions.forEach((option, index) => {
            if (!Number.isInteger(Number(option.quantity)) || Number(option.quantity) <= 0) next[`priceOption.${index}.quantity`] = t('recipes:validation_positive_integer');
            if (!Number.isFinite(Number(option.price)) || Number(option.price) < 0) next[`priceOption.${index}.price`] = t('recipes:validation_non_negative_number');
            if (activeOptions.filter(candidate => Number(candidate.quantity) === Number(option.quantity)).length > 1) next[`priceOption.${index}.quantity`] = t('recipes:validation_duplicate_value');
        });
        return next;
    }, [form, t]);

    const hasValidationErrors = Object.keys(errors).length > 0;
    const hasVisibleValidationErrors = submitted || Object.keys(errors).some(field => touched[field]);
    const markTouched = (field: string) => setTouched(previous => ({...previous, [field]: true}));
    const fieldError = (field: string) => serverErrors[field] || (errors[field] && (submitted || touched[field]) ? errors[field] : '');
    const fieldHasError = (field: string) => Boolean(fieldError(field));
    const changed = (current: unknown, original: unknown, invalid = false) => initialData != null && original !== undefined && JSON.stringify(current) !== JSON.stringify(original)
        ? {
            '& .MuiOutlinedInput-root': {
                '& fieldset': {borderColor: invalid ? 'error.main' : 'primary.main', borderWidth: 2},
                '&:hover fieldset': {borderColor: invalid ? 'error.dark' : 'primary.dark', borderWidth: 2},
                '&.Mui-focused fieldset': {borderColor: invalid ? 'error.dark' : 'primary.dark', borderWidth: 2},
            },
        }
        : undefined;

    const update = (patch: Partial<DecorationDraft>) => {
        setServerErrors({});
        setSubmitError('');
        setForm(previous => ({...previous, ...patch}));
    };
    const updateItem = (clientId: string, patch: Partial<DraftItem>) => {
        setServerErrors({});
        setSubmitError('');
        setForm(previous => ({
        ...previous,
        items: previous.items.map(item => item.clientId === clientId
            ? {...item, ...patch}
            : item),
        }));
    };
    const addItem = () => {
        const index = form.items.length;
        update({items: [...form.items, newItem()]});
        setTouched(previous => ({...previous, [`item.${index}.name`]: true, [`item.${index}.quantity`]: true}));
    };
    const addPriceOption = () => update({priceOptions: [...form.priceOptions, {clientId: crypto.randomUUID(), quantity: 1, price: 0}]});
    const updatePriceOption = (index: number, patch: Partial<DraftPriceOption>) => update({priceOptions: form.priceOptions.map((option, optionIndex) => optionIndex === index ? {...option, ...patch} : option)});
    const togglePriceOption = (index: number) => update({priceOptions: form.priceOptions.flatMap((option, optionIndex) => {
        if (optionIndex !== index) return [option];
        return option.id ? [{...option, deleted: !option.deleted}] : [];
    })});
    const removeItem = (clientId: string) => {
        setTouched({});
        setForm(previous => ({
            ...previous,
            items: previous.items.flatMap(item => item.clientId !== clientId ? [item] : item.id ? [{...item, deleted: !item.deleted}] : []),
        }));
    };
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setSubmitted(true);
        if (hasValidationErrors) return;
        setSaving(true);
        setServerErrors({});
        setSubmitError('');
        setTraceId('');
        try {
            await onSubmit(toSaveDecorationGuidRequest(form, Boolean(initialData)));
        } catch (error) {
            const kind = getApiErrorKind(error);
            const apiFields = getApiFieldErrors(error, t);
            const mapped = Object.fromEntries(Object.entries(apiFields).map(([field, message]) => {
                const itemMatch = /^items\[(\d+)]\.(itemId|name|quantity)$/i.exec(field);
                if (itemMatch) return [`item.${itemMatch[1]}.${itemMatch[2].toLowerCase() === 'quantity' ? 'quantity' : 'name'}`, message];
                return [field, message];
            }));
            setServerErrors(kind === 'validation' ? mapped : {});
            if (kind === 'server') setServerToast(getApiErrorMessage(error, t));
            else if (kind !== 'validation' || Object.keys(mapped).length === 0) setSubmitError(getApiErrorMessage(error, t));
            setTraceId(getApiTraceId(error));
        } finally {
            setSaving(false);
        }
    };

    const selectedIds = new Set(form.items.filter(item => !item.deleted).map(item => item.itemId));

    return <>
        <Box component="form" onSubmit={submit} noValidate sx={{display: 'grid', gap: 2, mt: 2}}>
            <Card variant="outlined"><CardContent><Stack spacing={2}>
                <Typography variant="h5">{t('recipes:decoration')}</Typography>
                <TextField required fullWidth sx={changed(form.name, initialData?.name, fieldHasError('name'))} label={t('recipes:name')} value={form.name} onChange={event => { markTouched('name'); update({name: event.target.value}); }} onBlur={() => markTouched('name')} error={fieldHasError('name')} helperText={fieldError('name') || ' '} FormHelperTextProps={{sx: animatedHelperSx(fieldHasError('name'))}}/>
                <TextField fullWidth multiline minRows={2} sx={changed(form.description, initialData?.description)} label={t('recipes:description')} value={form.description} onChange={event => update({description: event.target.value})}/>
            </Stack></CardContent></Card>

            <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="h6">{t('recipes:ingredients')}</Typography><WriteButton type="button" variant="outlined" startIcon={<AddIcon/>} onClick={addItem}>{t('recipes:add_new_item')}</WriteButton></Stack>

            {form.items.map((item, index) => {
                const original = initialData?.items?.find(entry => entry.id === item.id);
                const deleted = Boolean(item.deleted);
                const nameField = `item.${index}.name`;
                const quantityField = `item.${index}.quantity`;
                return (
                    <Card key={item.clientId} variant="outlined" sx={deleted ? {backgroundColor: 'rgba(211, 47, 47, 0.08)', borderColor: 'error.main'} : undefined}>
                        <CardContent><Box sx={ingredientRowSx}>
                            <Autocomplete disabled={deleted} sx={{gridArea: 'name', width: '100%', minWidth: 0, ...changed(item.itemId, original?.itemId, fieldHasError(nameField))}} fullWidth options={availableItems} value={availableItems.find(option => option.id === item.itemId) ?? null} isOptionEqualToValue={(option, value) => option.id === value.id} getOptionLabel={option => option.name} getOptionDisabled={option => selectedIds.has(option.id) && option.id !== item.itemId} disablePortal ListboxProps={{sx: {maxHeight: 280, overflowY: 'auto'}}} onChange={(_, value) => { markTouched(nameField); updateItem(item.clientId, {itemId: value?.id ?? '', name: value?.name ?? '', unit: value?.unitOfWeight ?? ''}); }} renderInput={params => <TextField {...params} required label={t('items:item_name_recipes')} onBlur={() => markTouched(nameField)} error={fieldHasError(nameField)} helperText={fieldError(nameField) || ' '} FormHelperTextProps={{sx: animatedHelperSx(fieldHasError(nameField))}}/>}/>
                            <LocalizedDecimalField disabled={deleted} fullWidth sx={{gridArea: 'quantity', width: '100%', minWidth: 0, ...changed(item.quantity, original?.quantity, fieldHasError(quantityField))}} label={t('items:count')} value={item.quantity} inputProps={{min: 0, step: 'any'}} InputProps={{endAdornment: selectedIngredientUnit(item.itemId, item.unit) ? <InputAdornment position="end">{selectedIngredientUnit(item.itemId, item.unit)}</InputAdornment> : undefined}} onValueChange={quantity => { markTouched(quantityField); updateItem(item.clientId, {quantity: quantity as number}); }} onBlur={() => markTouched(quantityField)} error={fieldHasError(quantityField)} helperText={fieldError(quantityField) || ' '} FormHelperTextProps={{sx: animatedHelperSx(fieldHasError(quantityField))}}/>
                            <WriteIconButton size="small" sx={ingredientActionSx} aria-label={t(deleted ? 'common:restore' : 'common:delete')} color={deleted ? 'success' : 'error'} onClick={() => removeItem(item.clientId)}>{deleted ? <RestoreIcon/> : <DeleteOutlineIcon/>}</WriteIconButton>
                        </Box></CardContent>
                    </Card>
                );
            })}

            <Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography variant="h6">{t('recipes:recommended_prices')}</Typography><Typography variant="body2" color="text.secondary">{t('recipes:decoration_prices_hint')}</Typography></Box><WriteButton type="button" variant="outlined" startIcon={<AddIcon/>} onClick={addPriceOption}>{t('recipes:add_price')}</WriteButton></Stack>
            {(form.priceOptions ?? []).map((option, index) => {
                const deleted = Boolean(option.deleted);
                return <Card key={option.clientId} variant="outlined" sx={deleted ? {backgroundColor: 'rgba(211, 47, 47, 0.08)', borderColor: 'error.main'} : undefined}><CardContent><Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems="flex-start">
                    <TextField disabled={deleted} fullWidth type="number" label={t('items:count')} value={option.quantity ?? ''} inputProps={{min: 1, step: 1}} error={fieldHasError(`priceOption.${index}.quantity`)} helperText={fieldError(`priceOption.${index}.quantity`) || undefined} onChange={event => { markTouched(`priceOption.${index}.quantity`); updatePriceOption(index, {quantity: event.target.value === '' ? NaN : Number(event.target.value)}); }}/>
                    <LocalizedDecimalField disabled={deleted} fullWidth label={t('recipes:recommended_price')} value={option.price} inputProps={{min: 0, step: 'any'}} InputProps={{endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>}} error={fieldHasError(`priceOption.${index}.price`)} helperText={fieldError(`priceOption.${index}.price`) || undefined} onValueChange={price => { markTouched(`priceOption.${index}.price`); updatePriceOption(index, {price: price ?? NaN}); }}/>
                    <WriteIconButton color={deleted ? 'success' : 'error'} onClick={() => togglePriceOption(index)}>{deleted ? <RestoreIcon/> : <DeleteOutlineIcon/>}</WriteIconButton>
                </Stack></CardContent></Card>;
            })}

            <Typography color="error" sx={animatedHelperSx(hasVisibleValidationErrors && hasValidationErrors)}>{hasVisibleValidationErrors && hasValidationErrors ? t('recipes:validation_fix_errors') : ' '}</Typography>
            {submitError && <Alert severity="error">{submitError}<TraceIdFeedback traceId={traceId}/></Alert>}
            <Box><WriteButton type="submit" variant="contained" disabled={saving || hasValidationErrors || (!!initialData && !isDirty)} endIcon={<SendIcon/>}>{t('common:save')}</WriteButton></Box>
        </Box>
        <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
            <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
        </Snackbar>
    </>;
}
